import React, { useEffect, useRef } from 'react';
import { FaWrench, FaShieldAlt, FaHandshake, FaClock, FaMotorcycle, FaCar } from 'react-icons/fa';

const AboutUs = () => {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === leftRef.current) {
              entry.target.classList.add('animate-slide-right');
            }
            if (entry.target === rightRef.current) {
              entry.target.classList.add('animate-slide-left');
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (leftRef.current) observer.observe(leftRef.current);
    if (rightRef.current) observer.observe(rightRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-gradient-to-br from-gray-50 via-white to-red-50 py-16 md:py-24"
    >
      {/* Background Pattern - Lighter */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-72 h-72 bg-red-400 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-300 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-red-200 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-12">
          {/* LEFT SIDE - Equal size with lighter background */}
          <div
            ref={leftRef}
            className="w-full lg:w-1/2 bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-red-200 shadow-xl opacity-0 transition-all duration-700"
            style={{ transform: 'translateX(-30px)' }}
          >
            <div className="h-full flex flex-col">
              {/* Small badge */}
              <div className="inline-flex items-center gap-2 bg-red-100 text-red-600 px-4 py-1.5 rounded-full w-fit mb-6">
                <FaWrench className="text-sm" />
                <span className="text-xs font-semibold uppercase tracking-wider">Trusted Service Since</span>
              </div>

              {/* Heading */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                <span className="text-gray-800">About </span>
                <span className="text-red-600 relative inline-block">
                  Us
                  <span className="absolute bottom-0 left-0 w-full h-1 bg-red-500 rounded-full"></span>
                </span>
              </h2>

              {/* Divider */}
              <div className="w-20 h-1 bg-red-500 rounded-full mb-6"></div>

              {/* Description - Darker text for better contrast */}
              <p className="text-gray-700 leading-relaxed mb-4">
                <span className="font-bold text-red-600">Ambris Bike & Car Service Center</span> is a trusted vehicle service provider located in Sathawa, Sarnath, Varanasi (Uttar Pradesh – 221007). We are dedicated to delivering reliable, affordable, and high-quality servicing solutions for both bikes and cars.
              </p>

              <p className="text-gray-700 leading-relaxed mb-4">
                With a focus on customer satisfaction, we provide complete maintenance and repair services under one roof. Our goal is to keep your vehicle running smoothly, safely, and efficiently at all times.
              </p>

              <p className="text-gray-700 leading-relaxed mb-4">
                We use the right tools, skilled technicians, and genuine parts to ensure every service meets professional standards. Whether it’s regular servicing, repair work, or quick checkups, we make the process simple, fast, and hassle-free.
              </p>

              <p className="text-gray-800 leading-relaxed font-medium">
                At Ambris Bike & Car Service Center, we believe in transparency, honest pricing, and long-term customer relationships.
              </p>

              <div className="mt-6 pt-4 border-t border-red-200">
                <p className="text-red-600 font-semibold italic">"Your vehicle’s care is our priority."</p>
              </div>

              {/* Feature tags - Lighter style */}
              <div className="flex flex-wrap gap-3 mt-6">
                <span className="bg-red-100 text-red-700 text-xs px-3 py-1 rounded-full">✓ Genuine Parts</span>
                <span className="bg-red-100 text-red-700 text-xs px-3 py-1 rounded-full">✓ Skilled Technicians</span>
                <span className="bg-red-100 text-red-700 text-xs px-3 py-1 rounded-full">✓ Transparent Pricing</span>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Lighter background */}
          <div
            ref={rightRef}
            className="w-full lg:w-1/2 bg-gradient-to-br from-white to-gray-100 rounded-2xl p-6 md:p-8 border border-red-200 shadow-xl opacity-0 transition-all duration-700"
            style={{ transform: 'translateX(30px)' }}
          >
            <div className="h-full flex flex-col">
              {/* Stats Grid - Lighter */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-red-50 rounded-xl p-4 text-center border border-red-200 hover:bg-red-100 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-red-600">500+</div>
                  <div className="text-gray-600 text-sm mt-1">Vehicles Serviced</div>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center border border-red-200 hover:bg-red-100 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-red-600">98%</div>
                  <div className="text-gray-600 text-sm mt-1">Happy Customers</div>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center border border-red-200 hover:bg-red-100 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-red-600">24/7</div>
                  <div className="text-gray-600 text-sm mt-1">Support Available</div>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center border border-red-200 hover:bg-red-100 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-red-600">10+</div>
                  <div className="text-gray-600 text-sm mt-1">Years Experience</div>
                </div>
              </div>

              {/* Service Icons - Lighter */}
              <div className="mb-8">
                <h3 className="text-gray-800 text-xl font-semibold mb-4 flex items-center gap-2">
                  <FaShieldAlt className="text-red-500" />
                  Our Specialties
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-3 hover:bg-red-50 transition-all duration-300">
                    <FaMotorcycle className="text-red-500 text-xl" />
                    <span className="text-gray-700 text-sm">Bike Servicing</span>
                  </div>
                  <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-3 hover:bg-red-50 transition-all duration-300">
                    <FaCar className="text-red-500 text-xl" />
                    <span className="text-gray-700 text-sm">Car Servicing</span>
                  </div>
                  <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-3 hover:bg-red-50 transition-all duration-300">
                    <FaWrench className="text-red-500 text-xl" />
                    <span className="text-gray-700 text-sm">Repair Works</span>
                  </div>
                  <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-3 hover:bg-red-50 transition-all duration-300">
                    <FaClock className="text-red-500 text-xl" />
                    <span className="text-gray-700 text-sm">Quick Checkups</span>
                  </div>
                </div>
              </div>

              {/* Location & Contact Card - Lighter */}
              <div className="mt-auto bg-white rounded-xl p-5 border border-red-200 shadow-md">
                <div className="flex items-start gap-3">
                  <div className="text-red-500 text-xl">📍</div>
                  <div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      <span className="font-semibold text-gray-900">Sathawa, Sarnath,</span><br />
                      Varanasi, Uttar Pradesh - 221007
                    </p>
                    <div className="flex gap-4 mt-3">
                      <button className="bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md">
                        Get Directions
                      </button>
                      <button className="border border-red-600 text-red-600 hover:bg-red-50 text-sm px-4 py-2 rounded-lg transition-all duration-300">
                        Contact Us
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes slideRight {
          0% {
            opacity: 0;
            transform: translateX(-30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideLeft {
          0% {
            opacity: 0;
            transform: translateX(30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-right {
          animation: slideRight 0.8s ease-out forwards !important;
        }
        .animate-slide-left {
          animation: slideLeft 0.8s ease-out forwards !important;
        }
      `}</style>
    </section>
  );
};

export default AboutUs;